import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import SharedHelmet from "../Helmet";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import MainCalculatorsList from "./MainCalculators";
const unitTranslations = {
  sqm: "متر مربع",
  sqkm: "كيلومتر مربع",
  sqcm: "سنتيمتر مربع",
  sqmm: "ميليمتر مربع",
  sqmcm: "مايكرومتر مربع",
  hectare: "هكتار",
  sqmile: "ميل مربع",
  sqyard: "ياردة مربعة",
  sqfoot: "قدم مربع",
  sqinch: "بوصة مربعة",
  acre: "فدان",
};
const unitSymbols = {
  sqm: "م²",
  sqkm: "كم²",
  sqcm: "سم²",
  sqmm: "ملم²",
  sqmcm: "ميكرومتر²",
  hectare: "هكتار",
  sqmile: "ميل²",
  sqyard: "ياردة²",
  sqfoot: "قدم²",
  sqinch: "بوصة²",
  acre: "فدان",
};
const conversionRates = {
  sqm: {
    sqm: 1,
    sqkm: 1e-6,
    sqcm: 1e4,
    sqmm: 1e6,
    sqmcm: 1e12,
    hectare: 1e-4,
    sqmile: 3.861e-7,
    sqyard: 1.19599,
    sqfoot: 10.7639,
    sqinch: 1550.0031,
    acre: 0.000247105,
  },
  sqkm: {
    sqm: 1e6,
    sqkm: 1,
    sqcm: 1e10,
    sqmm: 1e12,
    sqmcm: 1e18,
    hectare: 100,
    sqmile: 0.386102,
    sqyard: 1.19599e6,
    sqfoot: 1.07639e7,
    sqinch: 1.55e9,
    acre: 247.105,
  },
  sqcm: {
    sqm: 1e-4,
    sqkm: 1e-10,
    sqcm: 1,
    sqmm: 100,
    sqmcm: 1e8,
    hectare: 1e-8,
    sqmile: 3.861e-11,
    sqyard: 0.000119599,
    sqfoot: 0.00107639,
    sqinch: 0.15500031,
    acre: 2.47105e-8,
  },
  sqmm: {
    sqm: 1e-6,
    sqkm: 1e-12,
    sqcm: 0.01,
    sqmm: 1,
    sqmcm: 1e6,
    hectare: 1e-10,
    sqmile: 3.861e-13,
    sqyard: 1.19599e-6,
    sqfoot: 1.07639e-5,
    sqinch: 0.0015500031,
    acre: 2.47105e-10,
  },
  sqmcm: {
    sqm: 1e-12,
    sqkm: 1e-18,
    sqcm: 1e-8,
    sqmm: 1e-6,
    sqmcm: 1,
    hectare: 1e-16,
    sqmile: 3.861e-19,
    sqyard: 1.19599e-12,
    sqfoot: 1.07639e-11,
    sqinch: 1.55e-9,
    acre: 2.47105e-16,
  },
  hectare: {
    sqm: 10000,
    sqkm: 0.01,
    sqcm: 1e8,
    sqmm: 1e10,
    sqmcm: 1e16,
    hectare: 1,
    sqmile: 0.00386102,
    sqyard: 11959.9,
    sqfoot: 107639,
    sqinch: 1.55e7,
    acre: 2.47105,
  },
  sqmile: {
    sqm: 2.59e6,
    sqkm: 2.58999,
    sqcm: 2.59e10,
    sqmm: 2.59e12,
    sqmcm: 2.59e18,
    hectare: 258.999,
    sqmile: 1,
    sqyard: 3.0976e6,
    sqfoot: 2.788e7,
    sqinch: 4.014e9,
    acre: 640,
  },
  sqyard: {
    sqm: 0.836127,
    sqkm: 8.361e-7,
    sqcm: 8361.27,
    sqmm: 836127,
    sqmcm: 8.361e11,
    hectare: 8.36127e-5,
    sqmile: 3.22831e-7,
    sqyard: 1,
    sqfoot: 9,
    sqinch: 1296,
    acre: 0.000206612,
  },
  sqfoot: {
    sqm: 0.092903,
    sqkm: 9.2903e-8,
    sqcm: 929.03,
    sqmm: 92903,
    sqmcm: 9.29e10,
    hectare: 9.2903e-6,
    sqmile: 3.587e-8,
    sqyard: 0.111111,
    sqfoot: 1,
    sqinch: 144,
    acre: 2.2957e-5,
  },
  sqinch: {
    sqm: 0.00064516,
    sqkm: 6.4516e-10,
    sqcm: 6.4516,
    sqmm: 645.16,
    sqmcm: 6.4516e8,
    hectare: 6.4516e-8,
    sqmile: 2.491e-10,
    sqyard: 0.000771605,
    sqfoot: 0.00694444,
    sqinch: 1,
    acre: 1.5942e-7,
  },
  acre: {
    sqm: 4046.86,
    sqkm: 0.00404686,
    sqcm: 4.04686e7,
    sqmm: 4.04686e9,
    sqmcm: 4.04686e15,
    hectare: 0.404686,
    sqmile: 0.0015625,
    sqyard: 4840,
    sqfoot: 43560,
    sqinch: 6.27264e6,
    acre: 1,
  },
};
function AreaConverter() {
  const { fromAreaUnit, toAreaUnit } = useParams();
  const [amount, setAmount] = useState("");
  const [convertedAmount, setConvertedAmount] = useState(null);
  const [rate, setRate] = useState(null);
  const translatedFromUnit = unitTranslations[fromAreaUnit] || fromAreaUnit;
  const translatedToUnit = unitTranslations[toAreaUnit] || toAreaUnit;
  const fromSymbol = unitSymbols[fromAreaUnit] || "";
  const toSymbol = unitSymbols[toAreaUnit] || "";
  const TITLE = `تحويل من ${translatedFromUnit} إلى ${translatedToUnit} - محول المساحة`;
  const DESCRIPTION = `محول المساحة الدقيق لتحويل ${translatedFromUnit} إلى ${translatedToUnit}. حوّل بين وحدات المساحة المختلفة (متر مربع، كيلومتر مربع، فدان، هكتار) بسهولة مع معادلة التحويل وجدول شامل. أداة مجانية وسريعة.`;
  const KEYWORDS = `تحويل ${translatedFromUnit} إلى ${translatedToUnit}, ${translatedFromUnit} ${translatedToUnit}, محول المساحة, ${fromAreaUnit} to ${toAreaUnit}, حاسبة المساحة, متر مربع كيلومتر, فدان هكتار`;
  const OG_URL = `https://do-calculate.com/calculator/ar/unit_converter/area/${fromAreaUnit}/${toAreaUnit}/`;
  useEffect(() => {
    if (
      conversionRates[fromAreaUnit] &&
      conversionRates[fromAreaUnit][toAreaUnit]
    ) {
      setRate(conversionRates[fromAreaUnit][toAreaUnit]);
    } else {
      setRate(null);
    }
  }, [fromAreaUnit, toAreaUnit]);
  useEffect(() => {
    if (amount && !isNaN(amount) && rate) {
      setConvertedAmount(Number(amount) * rate);
    } else {
      setConvertedAmount(null);
    }
  }, [amount, rate]);
  const handleAmountChange = (e) => {
    setAmount(e.target.value);
  };
  const handleReset = () => {
    setAmount("");
    setConvertedAmount(null);
  };
  const amounts = [1, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000];
  const conversionTable = amounts.map((a) => ({
    fromAmount: a,
    toAmount: rate ? a * rate : 0,
  }));
  // Format numbers without scientific notation
  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) {
      return "0";
    }
    return num.toLocaleString(undefined, {
      maximumFractionDigits: 10,
      useGrouping: true,
    });
  };
  // Generate Schema.org structured data
  const generateSchemaData = () => {
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "الصفحة الرئيسية",
          item: "https://do-calculate.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "محولات الوحدات",
          item: "https://do-calculate.com/ar/unit_converter/",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "تحويل المساحة",
          item: "https://do-calculate.com/ar/unit_converter/area/",
        },
        {
          "@type": "ListItem",
          position: 4,
          name: `${translatedFromUnit} إلى ${translatedToUnit}`,
          item: OG_URL,
        },
      ],
    };
    const calculatorSchema = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: `محول ${translatedFromUnit} إلى ${translatedToUnit}`,
      description: DESCRIPTION,
      url: OG_URL,
      applicationCategory: "UtilityApplication",
      operatingSystem: "Any",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
    };
    return [breadcrumbSchema, calculatorSchema];
  };
  // Get conversion formula explanation
  const getFormulaExplanation = () => {
    if (!rate || rate === null) {
      return "جاري تحميل معامل التحويل...";
    }
    if (rate === 1) {
      return `${translatedFromUnit} و${translatedToUnit} هما نفس الوحدة، لذا القيمة تبقى كما هي.`;
    } else if (rate > 1) {
      return `لتحويل ${translatedFromUnit} إلى ${translatedToUnit}، اضرب القيمة في ${formatNumber(
        rate
      )}.`;
    } else {
      return `لتحويل ${translatedFromUnit} إلى ${translatedToUnit}، اضرب القيمة في ${formatNumber(
        rate
      )} (أو اقسم على ${formatNumber(1 / rate)}).`;
    }
  };
  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={generateSchemaData()}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "12px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>{TITLE}</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>
                  محول {translatedFromUnit} إلى {translatedToUnit}
                </strong>{" "}
                هو أداة دقيقة وسريعة لتحويل وحدات المساحة. حوّل{" "}
                <strong>{translatedFromUnit}</strong> إلى{" "}
                <strong>{translatedToUnit}</strong> بكل سهولة مع معادلة التحويل
                وجدول شامل للقيم الشائعة.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            <hr />
            <div className="row">
             <div className="col-sm-12 col-md-6 calculatorStyle">
                <div className="p-3 mb-3 bg-success text-white text-center rounded">
                  <h2 style={{ fontSize: "18px", margin: 0 }}>
                    {translatedFromUnit} → {translatedToUnit}
                  </h2>
                </div>
                <div
                  className="alert alert-info"
                  style={{ fontSize: "15px", lineHeight: "1.8" }}
                >
                  <strong>معادلة التحويل:</strong>
                  <br />1 {translatedFromUnit} = {rate ? formatNumber(rate) : "..."}{" "}
                  {translatedToUnit}
                  <br/>                  {getFormulaExplanation()}
                </div>
                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="form-group mb-3">
                    <label  htmlFor="amount">
                      <strong>أدخل المساحة بالـ {translatedFromUnit}:</strong>
                    </label>
                    <input
                      id="amount"
                      type="number"
                      className="form-control"
                      value={amount}
                      onChange={handleAmountChange}
                      placeholder={`مثال: 100`}
                      step="any"
                      style={{ fontSize: "16px", padding: "10px" }}
                    />
                  </div>
                  <hr />
                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleReset}
                      style={{ fontSize: "16px", padding: "10px 20px" }}
                    >
                      امسح
                    </button>
                    <Link
                      to={`/calculator/ar/unit_converter/area/${toAreaUnit}/${fromAreaUnit}/`}
                    >
                      <button
                        className="btn btn-outline-success"
                        style={{ fontSize: "15px", padding: "10px 15px" }}
                      >
                        🔄 عكس التحويل ({translatedToUnit} → {translatedFromUnit}
                        )
                      </button>
                    </Link>
                  </div>
                </form>
              </div>
              <div className="col-sm-12 col-md-4">
                {convertedAmount !== null && amount && (
                  <div className="calculatorStyle">
                    <div className="p-3 mb-3 bg-success text-white text-center rounded">
                      <h3 style={{ fontSize: "18px", margin: 0 }}>النتيجة</h3>
                    </div>
                    <div
                      className="alert alert-success"
                      style={{ fontSize: "16px", lineHeight: "1.8" }}
                    >
                      <div
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          backgroundColor: "#d4edda",
                          borderRadius: "8px",
                          marginBottom: "15px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "18px",
                            marginBottom: "10px",
                            color: "#155724",
                          }}
                        >
                          <strong>
                            {Number(amount).toLocaleString()} {fromSymbol}
                          </strong>
                        </div>
                        <div
                          style={{
                            fontSize: "32px",
                            fontWeight: "bold",
                            color: "#155724",
                          }}
                        >
                          = {formatNumber(convertedAmount)} {toSymbol}
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          marginTop: "15px",
                          backgroundColor: "#fff",
                          padding: "15px",
                          borderRadius: "8px",
                        }}
                      >
                        <strong>طريقة الحساب:</strong>
                        <div style={{ marginTop: "10px", lineHeight: "1.8" }}>
                          {Number(amount).toLocaleString()} × {rate ? formatNumber(rate) : "..."}
                          <br />= <strong>{formatNumber(convertedAmount)}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {!amount && (
                  <div className="alert alert-warning" role="alert">
                    <strong>ملاحظة:</strong> أدخل المساحة لرؤية النتيجة
                  </div>
                )}
              </div>
            </div>
            <hr />
            <div>
              <h3 style={{ fontSize: "18px", marginBottom: "15px" }}>
                جدول التحويل من {translatedFromUnit} إلى {translatedToUnit}
              </h3>
              <p
                className="alert alert-success"
                style={{ fontSize: "15px", lineHeight: "1.8" }}
              >
                كل <strong>1 {translatedFromUnit}</strong> يساوي{" "}
                <strong>{rate ? formatNumber(rate) : "..."} {translatedToUnit}</strong>. الجدول
                التالي يوضح التحويل للقيم الشائعة:
              </p>
              <div style={{ overflowX: "auto" }}>
                <table className="table table-bordered table-hover">
                  <thead>
                    <tr style={{ backgroundColor: "#e7f3fe" }}>
                      <th style={{ textAlign: "center", padding: "12px" }}>
                        {translatedFromUnit}
                      </th>
                      <th style={{ textAlign: "center", padding: "12px" }}>
                        {translatedToUnit}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {conversionTable.map((entry, index) => (
                      <tr key={index}>
                        <td style={{ textAlign: "center", padding: "10px" }}>
                          {entry.fromAmount.toLocaleString()}
                        </td>
                        <td style={{ textAlign: "center", padding: "10px" }}>
                          {formatNumber(entry.toAmount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <hr />
            <div
              className="alert alert-info"
              style={{ fontSize: "15px", lineHeight: "1.8" }}
            >
              💡 <strong>نصيحة:</strong> يمكنك أيضاً{" "}
              <Link to={`/calculator/ar/unit_converter/area/`}>
                استعراض جميع تحويلات المساحة المتاحة
              </Link>{" "}
              أو{" "}
              <Link
                to={`/calculator/ar/unit_converter/area/${toAreaUnit}/${fromAreaUnit}/`}
              >
                عكس التحويل من {translatedToUnit} إلى {translatedFromUnit}
              </Link>
              .
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>
      {/* Educational Content */}
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>
                  كيف تحول من {translatedFromUnit} إلى {translatedToUnit}؟
                </strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  تحويل <strong>{translatedFromUnit}</strong> إلى{" "}
                  <strong>{translatedToUnit}</strong> هو عملية بسيطة باستخدام معامل
                  التحويل الصحيح.
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  معادلة التحويل:
                </h3>
                <div
                  className="alert alert-success"
                  style={{ lineHeight: "1.8", backgroundColor: "#d4edda" }}
                >
                  <strong>القيمة بالـ {translatedToUnit}</strong> = القيمة بالـ{" "}
                  {translatedFromUnit} × {rate ? formatNumber(rate) : "..."}
                  <br/>                  <strong>أو بصيغة أخرى:</strong>
                  <br />1 {translatedFromUnit} = {rate ? formatNumber(rate) : "..."}{" "}
                  {translatedToUnit}
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  أمثلة عملية:
                </h3>
                <div
                  className="alert alert-info"
                  style={{ lineHeight: "1.8", backgroundColor: "#d1ecf1" }}
                >
                  <strong>مثال 1:</strong> تحويل 10 {translatedFromUnit} إلى{" "}
                  {translatedToUnit}
                  <br />
                  10 × {rate ? formatNumber(rate) : "..."} = {rate ? formatNumber(10 * rate) : "..."}{" "}
                  {translatedToUnit}
                  <br/>                  <strong>مثال 2:</strong> تحويل 100 {translatedFromUnit} إلى{" "}
                  {translatedToUnit}
                  <br />
                  100 × {rate ? formatNumber(rate) : "..."} = {rate ? formatNumber(100 * rate) : "..."}{" "}
                  {translatedToUnit}
                  <br/>                  <strong>مثال 3:</strong> تحويل 1000 {translatedFromUnit} إلى{" "}
                  {translatedToUnit}
                  <br />
                  1000 × {rate ? formatNumber(rate) : "..."} = {rate ? formatNumber(1000 * rate) : "..."}{" "}
                  {translatedToUnit}
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  الطريقة السريعة:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>استخدم الآلة الحاسبة أو المحول أعلاه</li>
                  <li>
                    اضرب المساحة بالـ {translatedFromUnit} في {rate ? formatNumber(rate) : "معامل التحويل"}
                  </li>
                  <li>
                    النتيجة ستكون المساحة بالـ {translatedToUnit}
                  </li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>
                  معلومات عن وحدات المساحة ({translatedFromUnit} و
                  {translatedToUnit})
                </strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  <strong>وحدات النظام المتري:</strong>
                </h3>
                <div
                  className="alert alert-success"
                  style={{ lineHeight: "1.8", backgroundColor: "#d4edda" }}
                >
                  <h4 style={{ fontSize: "16px", marginBottom: "10px" }}>
                    <strong>المتر المربع (م²):</strong>
                  </h4>
                  <ul>
                    <li>
                      <strong>الوحدة الأساسية</strong> للمساحة في النظام المتري
                    </li>
                    <li>1 م² = مساحة مربع طول ضلعه 1 متر</li>
                    <li>يستخدم لقياس مساحة الغرف، الشقق، الأراضي الصغيرة</li>
                  </ul>
                  <h4
                    style={{
                      fontSize: "16px",
                      marginTop: "15px",
                      marginBottom: "10px",
                    }}
                  >
                    <strong>الكيلومتر المربع (كم²):</strong>
                  </h4>
                  <ul>
                    <li>1 كم² = 1,000,000 م²</li>
                    <li>1 كم² = 100 هكتار</li>
                    <li>يستخدم لقياس المدن، المحافظات، الدول</li>
                  </ul>
                  <h4
                    style={{
                      fontSize: "16px",
                      marginTop: "15px",
                      marginBottom: "10px",
                    }}
                  >
                    <strong>الهكتار (هكتار):</strong>
                  </h4>
                  <ul>
                    <li>1 هكتار = 10,000 م²</li>
                    <li>1 هكتار = 0.01 كم²</li>
                    <li>يستخدم لقياس الأراضي الزراعية والمزارع</li>
                    <li>الوحدة المفضلة في الزراعة والعقارات</li>
                  </ul>
                  <h4
                    style={{
                      fontSize: "16px",
                      marginTop: "15px",
                      marginBottom: "10px",
                    }}
                  >
                    <strong>السنتيمتر المربع (سم²):</strong>
                  </h4>
                  <ul>
                    <li>1 م² = 10,000 سم²</li>
                    <li>يستخدم للمساحات الصغيرة (الورق، الشاشات)</li>
                  </ul>
                </div>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  <strong>وحدات النظام الإمبراطوري:</strong>
                </h3>
                <div
                  className="alert alert-info"
                  style={{ lineHeight: "1.8", backgroundColor: "#d1ecf1" }}
                >
                  <h4 style={{ fontSize: "16px", marginBottom: "10px" }}>
                    <strong>القدم المربع (قدم²):</strong>
                  </h4>
                  <ul>
                    <li>1 قدم² = 0.092903 م²</li>
                    <li>يستخدم في أمريكا لقياس مساحة المنازل والشقق</li>
                    <li>1 قدم² = 144 بوصة مربعة</li>
                  </ul>
                  <h4
                    style={{
                      fontSize: "16px",
                      marginTop: "15px",
                      marginBottom: "10px",
                    }}
                  >
                    <strong>الفدان (Acre):</strong>
                  </h4>
                  <ul>
                    <li>1 فدان = 4,046.86 م²</li>
                    <li>1 فدان = 0.404686 هكتار</li>
                    <li>1 فدان = 43,560 قدم مربع</li>
                    <li>يستخدم لقياس الأراضي الزراعية في أمريكا وبريطانيا</li>
                  </ul>
                  <h4
                    style={{
                      fontSize: "16px",
                      marginTop: "15px",
                      marginBottom: "10px",
                    }}
                  >
                    <strong>الميل المربع (ميل²):</strong>
                  </h4>
                  <ul>
                    <li>1 ميل² = 2.59 كم²</li>
                    <li>1 ميل² = 640 فدان</li>
                    <li>يستخدم لقياس المساحات الكبيرة جداً</li>
                  </ul>
                  <h4
                    style={{
                      fontSize: "16px",
                      marginTop: "15px",
                      marginBottom: "10px",
                    }}
                  >
                    <strong>الياردة المربعة (ياردة²):</strong>
                  </h4>
                  <ul>
                    <li>1 ياردة² = 0.836127 م²</li>
                    <li>1 ياردة² = 9 قدم مربع</li>
                    <li>تستخدم في البستنة والسجاد</li>
                  </ul>
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  جدول المقارنة:
                </h3>
                <table
                  className="table table-bordered"
                  style={{ width: "100%", marginTop: "10px" }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#e7f3fe" }}>
                      <th style={{ padding: "12px" }}>الوحدة</th>
                      <th style={{ textAlign: "center", padding: "12px" }}>
                        بالمتر المربع
                      </th>
                      <th style={{ padding: "12px" }}>الاستخدام</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: "10px" }}>1 سم²</td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        0.0001 م²
                      </td>
                      <td style={{ padding: "10px" }}>الأوراق، الشاشات</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "10px" }}>1 قدم²</td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        0.093 م²
                      </td>
                      <td style={{ padding: "10px" }}>الغرف، المنازل</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "10px" }}>1 م²</td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        1 م²
                      </td>
                      <td style={{ padding: "10px" }}>الأساسية</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "10px" }}>1 هكتار</td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        10,000 م²
                      </td>
                      <td style={{ padding: "10px" }}>المزارع</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "10px" }}>1 فدان</td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        4,047 م²
                      </td>
                      <td style={{ padding: "10px" }}>الأراضي الزراعية</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "10px" }}>1 كم²</td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        1,000,000 م²
                      </td>
                      <td style={{ padding: "10px" }}>المدن، الدول</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>استخدامات عملية لتحويل المساحة</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "15px" }}>
                  متى تحتاج لتحويل وحدات المساحة؟
                </h3>
                <div
                  className="alert alert-success"
                  style={{ lineHeight: "1.8", backgroundColor: "#d4edda" }}
                >
                  <h4 style={{ fontSize: "16px" }}>
                    <strong>في الحياة اليومية:</strong>
                  </h4>
                  <ul>
                    <li>
                      <strong>شراء أو تأجير عقار:</strong> فهم مساحة الشقة أو
                      الأرض بوحدات مختلفة
                    </li>
                    <li>
                      <strong>تخطيط الديكور:</strong> حساب كمية الطلاء، السجاد،
                      البلاط
                    </li>
                    <li>
                      <strong>الزراعة المنزلية:</strong> تحديد مساحة الحديقة
                    </li>
                    <li>
                      <strong>التسوق الدولي:</strong> فهم مقاسات المنتجات من
                      دول مختلفة
                    </li>
                  </ul>
                </div>
                <div
                  className="alert alert-info"
                  style={{ lineHeight: "1.8", backgroundColor: "#d1ecf1" }}
                >
                  <h4 style={{ fontSize: "16px" }}>
                    <strong>في المجالات المهنية:</strong>
                  </h4>
                  <ul>
                    <li>
                      <strong>العقارات:</strong> عرض المساحات بوحدات مختلفة
                      للعملاء
                    </li>
                    <li>
                      <strong>البناء والهندسة:</strong> حساب كميات المواد
                      (إسمنت، بلاط، دهان)
                    </li>
                    <li>
                      <strong>الزراعة:</strong> تخطيط المحاصيل وحساب الإنتاج
                    </li>
                    <li>
                      <strong>التخطيط العمراني:</strong> تصميم المدن والأحياء
                    </li>
                    <li>
                      <strong>الديكور الداخلي:</strong> حساب احتياجات الأرضيات
                      والجدران
                    </li>
                    <li>
                      <strong>المساحة:</strong> إعداد الخرائط والمخططات
                    </li>
                  </ul>
                </div>
                <div
                  className="alert alert-warning"
                  style={{ lineHeight: "1.8", backgroundColor: "#fff3cd" }}
                >
                  <h4 style={{ fontSize: "16px" }}>
                    <strong>نصائح مهمة:</strong>
                  </h4>
                  <ul>
                    <li>
                      <strong>للشقق والمنازل:</strong> استخدم المتر المربع في
                      معظم الدول العربية
                    </li>
                    <li>
                      <strong>للأراضي الزراعية:</strong> الهكتار أو الفدان
                      هما الأكثر شيوعاً
                    </li>
                    <li>
                      <strong>في أمريكا:</strong> القدم المربع للمباني
                      والفدان للأراضي
                    </li>
                    <li>
                      <strong>عند الشراء:</strong> تأكد من الوحدة المستخدمة
                      في العقد
                    </li>
                    <li>
                      <strong>حساب كمية البلاط:</strong> أضف 10% للهدر
                      والقطع
                    </li>
                    <li>
                      <strong>حساب الدهان:</strong> كل لتر يغطي حوالي 10-12
                      م²
                    </li>
                  </ul>
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  أمثلة عملية:
                </h3>
                <table
                  className="table table-bordered"
                  style={{ width: "100%", marginTop: "10px" }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#e7f3fe" }}>
                      <th style={{ padding: "12px" }}>المثال</th>
                      <th style={{ textAlign: "center", padding: "12px" }}>
                        المساحة
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: "10px" }}>غرفة نوم متوسطة</td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        12-15 م² (130-160 قدم²)
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: "10px" }}>شقة صغيرة</td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        50-70 م² (540-750 قدم²)
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: "10px" }}>فيلا متوسطة</td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        250-350 م²
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: "10px" }}>ملعب كرة قدم</td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        7,140 م² (حوالي 0.7 هكتار)
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: "10px" }}>مزرعة صغيرة</td>
                      <td style={{ textAlign: "center", padding: "10px" }}>
                        5-10 هكتار (50,000-100,000 م²)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
         </div>
</div>
  );
}
export default AreaConverter;